//modules
import React, { useRef, useState } from 'react';
import { Box, useDisclosure } from '@chakra-ui/react';
import { useReactToPrint } from 'react-to-print';

//common components
import Modal from '../../../common/BasicModal';
import GspPrintPreview from './GspPrintPreview';
import CashPrintPreview from './CashPrintPreview';
import CertificateRework from './CertificateRework';
import CertificatePreview from './CertificatePreview';
import DataTable from '../../../common/certificateComponents/DataTable';

//utility & actions
import {
  FETCH_PRODUCTION_CERTIFICATE_LOGS,
  FETCH_PRODUCTION_CERTIFICATE,
} from '../../../../store/actions/actionTypes';
import {
  CERTIFICATE_LOGS_STATUS,
  CERTIFICATE_OF_PRODUCTION_TYPES,
  LOGS_TABLE_DISPLAY_SIZE,
} from '../../../../constants/certificateConstant';
import { useDispatch, useSelector } from 'react-redux';
import { BADGE_STATE_COLORS } from '../../../../constants/dataTableConstants';
import { getProductionCertLogs, getProductionCertificate } from '../../../../api/certificate';

const CertificateLogTable = () => {
  const dispatch = useDispatch();
  const gspCertPrintRef = useRef();
  const cashCertPrintRef = useRef();
  const [printData, setPrintData] = useState();
  const mill = useSelector((state) => state.mill.mill);
  const [modalComponent, setModalComponent] = useState();
  const data = useSelector((state) => state.certificate.productionCertLogs);
  const { isOpen, onOpen, onClose } = useDisclosure(); //control hook for modal

  //configure columns
  const columns = [
    {
      header: 'Date',
      accessor: 'usedOn',
      isDate: true,
    },
    {
      header: 'Number',
      accessor: 'certificateNum',
    },
    {
      header: 'Export Lc',
      accessor: 'masterLc',
    },
    {
      header: 'Value (In USD)',
      accessor: 'valueInUsd',
    },
    {
      header: 'Type',
      accessor: 'type',
    },
    {
      header: 'Product',
      accessor: 'productType',
    },
    {
      header: 'Status',
      accessor: 'applicationStatus',
      isBadge: true,
      colorScheme: BADGE_STATE_COLORS,
      hoverInfo: ['remarks'],
    },
    {
      header: 'Action',
      accessor: 'applicationStatus',
      isButton: true,
      actionScheme: [
        { group: 'APPLIED', variant: 'read', action: 'VIEW' },
        { group: 'IN REVIEW', variant: 'read', action: 'VIEW' },
        { group: 'APPROVED', variant: 'print', action: 'PRINT' },
        { group: 'REWORK', variant: 'write', action: 'EDIT' },
      ],
    },
  ];

  const handleView = async ({ rowObject }) => {
    setModalComponent(<CertificatePreview data={rowObject} />);
    onOpen();
  };

  const handleEdit = async ({ rowObject }) => {
    const res = await getProductionCertificate({
      certId: rowObject.certificateNum,
      certType: rowObject.type,
    });
    if (res.data?.status === 200) {
      dispatch({ type: FETCH_PRODUCTION_CERTIFICATE, payload: res.data.certificate });
    }
    setModalComponent(
      <CertificateRework
        close={onClose}
        updateTable={() => handleSearch({ page: 1, pageSize: 10 })}
      />,
    );
    onOpen();
  };

  const handlePagination = async ({ page, pageSize, searchKey, selected, startDate, endDate }) => {
    dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_LOGS, payload: null });
    const params = {
      page,
      pageSize,
      searchKey,
      type: selected?.['Certificate Type'],
      status: selected?.['Application Status'],
      startDate,
      endDate,
    };
    const res = await getProductionCertLogs(params);
    if (res.data) {
      dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_LOGS, payload: res.data });
    }
  };

  const handleSearch = async ({ searchKey, selected, startDate, endDate }) => {
    dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_LOGS, payload: null });
    const params = {
      searchKey,
      type: selected?.['Certificate Type'],
      status: selected?.['Application Status'],
      startDate,
      endDate,
    };
    const res = await getProductionCertLogs(params);
    if (res.data) {
      dispatch({ type: FETCH_PRODUCTION_CERTIFICATE_LOGS, payload: res.data });
    }
  };

  const printGspCertificate = useReactToPrint({
    documentTitle: `BTMA-GSP-Certificate-${printData?.millName}-${printData?.certData?.certificateNum}`,
    content: () => gspCertPrintRef.current,
  });

  const printCashIncentiveCertificate = useReactToPrint({
    documentTitle: `BTMA-Cash-Incentive-Certificate-${printData?.millName}-${printData?.certData?.certificateNum}`,
    content: () => cashCertPrintRef.current,
  });

  const handlePrint = ({ rowObject }) => {
    setPrintData({
      millName: mill?.millName,
      millAddress: mill?.hoInfo?.hoName,
      factoryAddress: mill?.msInfo?.msAddress,
      type: rowObject?.productType,
      certData: rowObject,
    });
    setTimeout(() => {
      switch (rowObject.type) {
        case 'GSP':
          printGspCertificate();
          break;
        case 'CASH':
          printCashIncentiveCertificate();
          break;
        default:
          return null;
      }
    }, 500);
  };

  //assign actions to each button
  const actionObject = {
    VIEW: handleView,
    EDIT: handleEdit,
    PRINT: handlePrint,
  };

  return (
    <Box mt={6}>
      <DataTable
        caption="Certificate Logs"
        columns={columns}
        data={data?.certificates}
        topPanel={true}
        actions={actionObject}
        isPaginated
        pageSizes={LOGS_TABLE_DISPLAY_SIZE}
        entries={data?.totalEntries}
        currentPage={data?.page}
        currentPageSize={data?.pageSize}
        keySearch
        selectOptions={[CERTIFICATE_OF_PRODUCTION_TYPES, CERTIFICATE_LOGS_STATUS]}
        dateRange
        paginate={handlePagination}
        search={handleSearch}
      />
      <Modal mountControl={isOpen} close={onClose}>
        {modalComponent}
      </Modal>
      <Box display="none">
        <GspPrintPreview ref={gspCertPrintRef} data={printData} />
        <CashPrintPreview ref={cashCertPrintRef} data={printData} />
      </Box>
    </Box>
  );
};

export default CertificateLogTable;
